import { createSelector } from '@reduxjs/toolkit';
import { UnifiedLiquidityEnum } from '@talos/kyoko';
import type { AppState } from '../../../../providers/AppStateProvider/types';
import type { GetOrderMarketSelectorItemsParams } from '../../../MarketSelector/Orders/useOrderMarketSelectorItems';
import { subAccountFieldsToAllocations } from '../../utils';

/** A selector which collects the params necessary for building market selector items for the order form */
export const selectBuildOrderMarketSelectorItemsParams = createSelector(
  [
    // Pieces of order/form state
    (state: AppState) => state.order.form.symbolField.value,
    (state: AppState) => state.order.form.symbolField.value?.Symbol,
    (state: AppState) => state.order.form.strategyField.value?.Name,
    (state: AppState) => state.order.form.allocations,
    (state: AppState) => state.order.form.parameters.UnifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled,

    // Various settings
    (state: AppState) => state.referenceData.settings.allowSyntheticCcy,
    (state: AppState) => state.referenceData.settings.allowSyntheticCrosses,
    (state: AppState) => state.referenceData.settings.enableAccountSegregation,

    // Ref data state
    (state: AppState) => state.referenceData.securities.securitiesBySymbol,
    (state: AppState) => state.referenceData.markets.marketsByName,
    (state: AppState) => state.referenceData.marketAccounts.marketAccountsByMarket,
    (state: AppState) => state.referenceData.marketAccounts.marketAccountsByName,
    (state: AppState) => state.referenceData.marketAccounts.marketAccountsList,
    (state: AppState) => state.referenceData.fixingIndices.marketsByFixingIndex,
    (state: AppState) => state.referenceData.strategies.strategiesByName,
    (state: AppState) => state.referenceData.marketAccountPermissionFilters.marketAccountFilterIDsBySubAccountName,
    (state: AppState) => state.referenceData.marketAccountPermissionFilters.marketAccountPermissionFiltersByFilterID,
    (state: AppState) => state.referenceData.unifiedLiquidityTokens,
  ],
  (
    security,
    symbol,
    strategy,
    allocations,
    isUnifiedLiquidityEnabled,

    allowSyntheticCrossCurrency,
    allowSyntheticCrosses,
    enableAccountSegregation,

    securitiesBySymbol,
    marketsByName,
    marketAccountsByMarket,
    marketAccountsByName,
    marketAccountsList,
    marketsByFixingIndex,
    strategiesByName,
    marketAccountFilterIDsBySubAccountName,
    marketAccountPermissionFiltersByFilterID,
    unifiedLiquidityTokenBySymbol
  ) => {
    return {
      security,
      symbol,
      strategy,
      subAccountAllocations: subAccountFieldsToAllocations(allocations),
      isUnifiedLiquidityEnabled,

      allowSyntheticCrossCurrency,
      allowSyntheticCrosses,
      enableAccountSegregation,

      securitiesBySymbol,
      marketsByName,
      marketAccountsByMarket,
      marketAccountsByName,
      marketAccountsList,
      marketsByFixingIndex,
      strategiesByName,
      marketAccountFilterIDsBySubAccountName,
      marketAccountPermissionFiltersByFilterID,
      unifiedLiquidityTokenBySymbol,
    } satisfies Omit<GetOrderMarketSelectorItemsParams, 'mssByAccountBySymbol' | 'enableMSSValidation'>;
  }
);

/**
 * Create a selector during run-time to select params for your given legIndex. We need the makeSelect pattern because
 * we need to perform a unique selection per legIndex, and we don't want to hard-code indexes 0 and 1
 */
export const makeSelectBuildOrderMarketSelectorItemsParams = (legIndex: number | undefined) =>
  createSelector([selectBuildOrderMarketSelectorItemsParams, (_: AppState) => legIndex], (params, legIndex) => {
    // For ease of use, we take the base order-level params, and then override with things related to our leg index
    // This'll be any UL details, and the security of course, and possibly more in the future

    // We're "lucky" to be able to just grab all the metadata we need out of the params needed for building the items...
    // Otherwise, we would've needed another selector to give us this information
    const multilegSecurity = params.security;
    let legSymbol: string | undefined = undefined;
    if (legIndex === undefined) {
      // ul case, look up thru ul tokens. We just do .at(0), multileg only supports one ul symbol
      legSymbol = multilegSecurity?.Symbol
        ? params.unifiedLiquidityTokenBySymbol?.get(multilegSecurity?.Symbol)?.Tokens.at(0)?.Symbol
        : undefined;
    } else {
      legSymbol = multilegSecurity?.MultilegDetails?.Legs.at(legIndex)?.Symbol;
    }

    const legSecurity = legSymbol ? params.securitiesBySymbol.get(legSymbol) : undefined;

    return {
      ...params,

      // UL is not relevant for individual legs' items
      isUnifiedLiquidityEnabled: false,
      unifiedLiquidityTokenBySymbol: undefined,

      // override the security passed with the one relevant for our leg
      security: legSecurity,
      symbol: legSymbol,
    };
  });
